// ============================================================
// Icone.js — Ícones de traço do painel
//
// Substituem os emojis que o menu e os cabeçalhos usavam. Emoji parece
// ícone e não é: muda de desenho conforme o sistema operacional, não
// aceita a cor da tela e sempre aparece um degrau maior ou menor do que o
// texto ao lado. O mesmo painel abria com um desenho no Windows e outro no
// celular, e não havia como alinhar nada com isso.
//
// Todos partem do mesmo traço (1,75px, pontas arredondadas) e herdam a cor
// de quem os usa, então o mesmo ícone serve no menu escuro e no cabeçalho
// claro sem uma segunda versão.
// ============================================================

// As formas, em coordenadas de uma caixa 24×24. Só o miolo do <svg>: o
// invólucro é igual para todas e não precisa ser repetido linha a linha.
const FORMAS = {
  casa: <><path d="M3 10.5 12 3l9 7.5" /><path d="M5 9.8V20a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V9.8" /><path d="M9.5 21v-6h5v6" /></>,
  predio: <><rect x="4" y="3" width="16" height="18" rx="2" /><path d="M8 7h2M14 7h2M8 11h2M14 11h2M8 15h2M14 15h2" /></>,
  usuarios: <><circle cx="9" cy="8" r="3.2" /><path d="M3 20c0-3.2 2.7-5.2 6-5.2s6 2 6 5.2" /><path d="M16.5 5.2a3.2 3.2 0 0 1 0 5.9" /><path d="M18 14.6c2 .8 3 2.5 3 5.4" /></>,
  rosto: <><path d="M3 8V5.5A1.5 1.5 0 0 1 4.5 4H7" /><path d="M17 4h2.5A1.5 1.5 0 0 1 21 5.5V8" /><path d="M21 16v2.5a1.5 1.5 0 0 1-1.5 1.5H17" /><path d="M7 20H4.5A1.5 1.5 0 0 1 3 18.5V16" /><circle cx="12" cy="12" r="3.6" /><path d="M9.6 17.2a4.6 4.6 0 0 1 4.8 0" /></>,
  calendarioCheck: <><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 9.5h18M8 3v4M16 3v4" /><path d="m9 14.8 2.2 2.2 4-4.2" /></>,
  calendario: <><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 9.5h18M8 3v4M16 3v4" /><path d="M7.5 13h2M11 13h2M14.5 13h2M7.5 17h2M11 17h2" /></>,
  envelope: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3.6 6.5 7.5 5.6a1.5 1.5 0 0 0 1.8 0l7.5-5.6" /></>,
  pino: <><path d="M12 21s7-5.6 7-11a7 7 0 1 0-14 0c0 5.4 7 11 7 11Z" /><circle cx="12" cy="10" r="2.6" /></>,
  barras: <><path d="M4 20h16" /><rect x="5.5" y="11" width="3.5" height="6" rx="1" /><rect x="10.2" y="6.5" width="3.5" height="10.5" rx="1" /><rect x="15" y="9" width="3.5" height="8" rx="1" /></>,
  linha: <><path d="M3 18.5h18" /><path d="m4.5 15 4.2-5 3.4 3.2L20 5.5" /><path d="M16.4 5.5H20v3.6" /></>,
  grade: <><rect x="3.5" y="3.5" width="7" height="7" rx="1.6" /><rect x="13.5" y="3.5" width="7" height="7" rx="1.6" /><rect x="3.5" y="13.5" width="7" height="7" rx="1.6" /><rect x="13.5" y="13.5" width="7" height="7" rx="1.6" /></>,
  prancheta: <><rect x="4.5" y="4" width="15" height="17" rx="2" /><path d="M9 4V3a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v1" /><path d="M8.5 10h7M8.5 14h7M8.5 18h4" /></>,
  halter: <><path d="M3 9.5v5M6.5 7v10M17.5 7v10M21 9.5v5" /><path d="M6.5 12h11" /></>,
  relogio: <><circle cx="12" cy="12" r="8.5" /><path d="M12 7.2V12l3.2 2" /></>,
  pulso: <><path d="M3 12.5h3.8l2-5.2 3.2 9.4 2.4-6 1.6 1.8H21" /></>,
  trofeu: <><path d="M7.5 4h9v5.2a4.5 4.5 0 0 1-9 0Z" /><path d="M7.5 5.5H5a2 2 0 0 0 0 4h1" /><path d="M16.5 5.5H19a2 2 0 0 1 0 4h-1" /><path d="M12 13.8V17" /><path d="M8.5 20.5h7" /><path d="M9.6 20.5c0-1.6 1-2.6 2.4-2.6s2.4 1 2.4 2.6" /></>,
  bandeira: <><path d="M5.5 21V3.8" /><path d="M5.5 4.5h12l-2.2 3.6L17.5 12h-12Z" /></>,
  alvo: <><circle cx="12" cy="12" r="8.4" /><circle cx="12" cy="12" r="4.6" /><circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" /></>,
  regua: <><rect x="2.6" y="8.4" width="18.8" height="7.2" rx="1.8" /><path d="M7 8.4v3M11 8.4v4.4M15 8.4v3M19 8.4v4.4" /></>,
  medalha: <><circle cx="12" cy="15" r="5.2" /><path d="m8.4 10.4-2.8-6h4.2L12 8" /><path d="m15.6 10.4 2.8-6h-4.2L12 8" /><path d="m11 15 1 1 2-2.2" /></>,
  check: <><circle cx="12" cy="12" r="8.6" /><path d="m8.4 12.3 2.4 2.4 4.8-5" /></>,
  engrenagem: <><circle cx="12" cy="12" r="3.1" /><path d="M12 2.8v2.4M12 18.8v2.4M21.2 12h-2.4M5.2 12H2.8M18.5 5.5l-1.7 1.7M7.2 16.8l-1.7 1.7M18.5 18.5l-1.7-1.7M7.2 7.2 5.5 5.5" /></>,
  busca: <><circle cx="10.8" cy="10.8" r="6.6" /><path d="m15.6 15.6 4.4 4.4" /></>,
  mais: <><circle cx="12" cy="12" r="8.6" /><path d="M12 8.4v7.2M8.4 12h7.2" /></>,
  chave: <><circle cx="8.2" cy="12" r="4.2" /><path d="M12.4 12H21" /><path d="M17.6 12v3.4M20.2 12v2.6" /></>,
  celular: <><rect x="6.8" y="2.6" width="10.4" height="18.8" rx="2.4" /><path d="M10.6 5.4h2.8" /><path d="M11 18.4h2" /></>,
  sair: <><path d="M14.5 8V5.6a1.6 1.6 0 0 0-1.6-1.6H6.1A1.6 1.6 0 0 0 4.5 5.6v12.8A1.6 1.6 0 0 0 6.1 20h6.8a1.6 1.6 0 0 0 1.6-1.6V16" /><path d="M10 12h10" /><path d="m17 9 3 3-3 3" /></>,
  reticencias: <><circle cx="12" cy="5.4" r="1.5" fill="currentColor" stroke="none" /><circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" /><circle cx="12" cy="18.6" r="1.5" fill="currentColor" stroke="none" /></>,
  chevronBaixo: <path d="m6.5 9.5 5.5 5.5 5.5-5.5" />,
  chevronDireita: <path d="m9.5 6.5 5.5 5.5-5.5 5.5" />,
  chevronEsquerda: <path d="M14.5 6.5 9 12l5.5 5.5" />,
};

// Ordenação: as duas pontas de seta que o cabeçalho de tabela usa. Fora do
// mapa porque é a única que pinta em vez de traçar, e porque precisa saber
// qual das duas está ativa.
export function IconeOrdenar({ direcao = null, tamanho = 12 }) {
  const aceso = "#334155", apagado = "#CBD5E1";
  return (
    <svg width={tamanho} height={tamanho} viewBox="0 0 12 12" aria-hidden="true"
      style={{ flexShrink: 0, display: "block" }}>
      <path d="M6 1.4 8.6 4.8H3.4Z" fill={direcao === "asc" ? aceso : apagado} />
      <path d="M6 10.6 3.4 7.2h5.2Z" fill={direcao === "desc" ? aceso : apagado} />
    </svg>
  );
}

export default function Icone({ nome, tamanho = 18, cor = "currentColor", style = {} }) {
  const forma = FORMAS[nome];
  if (!forma) return null;
  return (
    <svg
      width={tamanho} height={tamanho} viewBox="0 0 24 24" fill="none"
      stroke={cor} strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" focusable="false"
      style={{ flexShrink: 0, display: "block", ...style }}
    >
      {forma}
    </svg>
  );
}

export const NOMES_DE_ICONE = Object.keys(FORMAS);
